<template>
  <EditDialog
    v-model="dialog"
    :save-button-text="$t(TEMPLATE_TYPE_ACTION_TITLES[template?.type || ''])"
    :title="$t('newTask')"
    :max-width="1080"
    :min-content-height="560"
    content-class="NewTaskDialogShell"
    no-body-paddings
    @save="closeDialog"
    @close="closeDialog"
    test-id="newTaskDialog"
  >
    <template v-slot:title={}>
      <div class="NewTaskDialog__title">
        <div class="NewTaskDialog__mark">
          <ZpIcon :name="TEMPLATE_TYPE_ICONS[template?.type || '']" :size="18" />
        </div>
        <div class="NewTaskDialog__heading">
          <span class="zp-overline">{{ $t('launchTask') }}</span>
          <strong>{{ template?.name || templateTitle }}</strong>
          <small class="zp-tabular">
            {{ $t('templateId') }} {{ templateId }}
          </small>
        </div>
        <div class="NewTaskDialog__chips">
          <span class="NewTaskDialog__chip">
            <small>{{ $t('application') }}</small>
            <strong>{{ appTitle }}</strong>
          </span>
          <span class="NewTaskDialog__chip">
            <small>{{ $t('project') }}</small>
            <strong>#{{ projectId }}</strong>
          </span>
          <span class="NewTaskDialog__chip">
            <small>{{ $t('task2') }}</small>
            <strong>{{ $t(TEMPLATE_TYPE_ACTION_TITLES[template?.type || '']) }}</strong>
          </span>
        </div>
      </div>
    </template>

    <template v-slot:form="{ onSave, onError, needSave, needReset }">
      <TaskForm
        :project-id="projectId"
        item-id="new"
        :template="template"
        @save="onSave"
        @error="onError"
        :need-save="needSave"
        :need-reset="needReset"
        :source-task="sourceTask"
      />
    </template>
  </EditDialog>
</template>
<style lang="scss">
.NewTaskDialogShell {
  .v-card {
    overflow: hidden;
    border: var(--zp-border-hair) solid var(--zp-border-default);
    border-radius: var(--zp-radius-md) !important;
    background: var(--zp-bg-surface) !important;
    color: var(--zp-text-primary);
  }

  .v-card__title {
    min-height: 132px;
    margin-right: 0 !important;
    padding: var(--zp-space-lg) 72px var(--zp-space-lg) var(--zp-space-lg) !important;
    border-bottom: var(--zp-border-hair) solid var(--zp-border-hairline);
    white-space: normal !important;
    overflow: visible !important;
  }

  .item-dialog__title-actions {
    top: var(--zp-space-lg);
    right: var(--zp-space-lg);
    background: transparent !important;
  }

  .v-card__actions {
    min-height: 70px;
    padding: var(--zp-space-sm) var(--zp-space-lg) !important;
    border-top: var(--zp-border-hair) solid var(--zp-border-hairline);
    background: var(--zp-bg-page);

    .v-btn[data-testid="editDialog-save"].v-btn--text {
      min-width: 132px;
      border: var(--zp-border-hair) solid var(--zp-brand) !important;
      background: var(--zp-brand) !important;
      color: var(--zp-brand-on) !important;
    }
  }
}

.NewTaskDialog {
  &__title {
    display: grid;
    grid-template-columns: 44px minmax(0, 1fr) auto;
    align-items: center;
    gap: var(--zp-space-md);
    width: 100%;
    min-width: 0;
  }

  &__mark {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: var(--zp-radius-md);
    background: var(--zp-brand-soft);
    color: var(--zp-brand);
  }

  &__heading {
    display: grid;
    gap: var(--zp-space-2xs);
    min-width: 0;

    strong {
      color: var(--zp-text-primary);
      font-family: var(--zp-font-display);
      font-size: var(--zp-text-xl);
      font-weight: 650;
      line-height: var(--zp-leading-tight);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    small {
      color: var(--zp-text-tertiary);
      font-size: var(--zp-text-xs);
    }
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: var(--zp-space-sm);
    justify-content: flex-end;
  }

  &__chip {
    min-width: 132px;
    padding: var(--zp-space-xs) var(--zp-space-sm);
    border: var(--zp-border-hair) solid var(--zp-border-hairline);
    border-radius: var(--zp-radius-md);
    background: var(--zp-bg-page);

    small,
    strong {
      display: block;
      line-height: var(--zp-leading-snug);
    }

    small {
      color: var(--zp-text-tertiary);
      font-size: var(--zp-text-2xs);
      font-weight: 700;
      letter-spacing: var(--zp-tracking-overline);
      text-transform: uppercase;
    }

    strong {
      margin-top: 2px;
      color: var(--zp-text-primary);
      font-size: var(--zp-text-sm);
      font-weight: 650;
    }
  }
}

@media (max-width: 700px) {
  .NewTaskDialogShell {
    max-height: calc(100vh - 80px);
    margin: 16px !important;

    .v-card {
      display: flex;
      flex-direction: column;
      max-height: calc(100vh - 80px);
    }

    .v-card__title {
      flex: 0 0 auto;
      min-height: auto;
      padding: var(--zp-space-md) 56px var(--zp-space-md) var(--zp-space-md) !important;
    }

    .v-card__text {
      flex: 1 1 auto;
      min-height: 0 !important;
      overflow-y: auto;
    }

    .v-card__actions {
      flex: 0 0 auto;
    }
  }

  .NewTaskDialog {
    &__title {
      grid-template-columns: 36px minmax(0, 1fr);
    }

    &__chips {
      grid-column: 1 / -1;
      justify-content: stretch;
    }

    &__chip {
      flex: 1 1 100px;
      min-width: 0;
    }
  }
}
</style>
<script>
import {
  APP_SHORT_TITLE,
  TEMPLATE_TYPE_ACTION_TITLES,
  TEMPLATE_TYPE_ICONS,
} from '@/lib/constants';
import TaskForm from './TaskForm.vue';
import EditDialog from './EditDialog.vue';

import EventBus from '../event-bus';

export default {
  components: {
    TaskForm,
    EditDialog,
  },
  props: {
    value: Boolean,
    projectId: Number,
    template: Object,
    sourceTask: Object,
  },
  data() {
    return {
      dialog: false,
      TEMPLATE_TYPE_ACTION_TITLES,
      TEMPLATE_TYPE_ICONS,
    };
  },
  watch: {
    async dialog(val) {
      this.$emit('input', val);
    },

    async value(val) {
      this.dialog = val;
    },
  },

  computed: {
    templateTitle() {
      let res = this.template?.name || '';
      if (res.length > 16) {
        res = `${res.substring(0, 14)}...`;
      }

      return res;
    },

    templateId() {
      const id = this.template?.id;
      return id == null ? '—' : String(id).padStart(4, '0');
    },

    appTitle() {
      return APP_SHORT_TITLE[this.template?.app] || this.template?.app || 'Ansible';
    },
  },

  methods: {
    closeDialog(e) {
      this.dialog = false;
      if (e) {
        EventBus.$emit('i-show-task', {
          taskId: e.item.id,
        });
        this.$emit('save', e);
      }
      this.$emit('close');
    },
  },
};
</script>
